import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Grid, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Notification } from 'clipsal-cortex-types/src/api/api-notifications';

import { CircleCheckbox } from '../../common/components/CircleCheckbox';
import { OptionSelectAlertDrawer } from '../../common/components/OptionSelectAlertDrawer';
import { SwipeableSEHomeCard } from '../../common/components/SwipeableSEHomeCard';
import { ArchiveIcon, DeleteIcon } from '../../styles/custom-icons';
import { selectSite } from '../site/siteSlice';
import { useGetNotificationTitle } from './notifications-helper';
import { useDeleteNotificationByIdMutation, useUpdateNotificationByIdMutation } from './notificationsApi';
import { NotificationTimestamp } from './NotificationTimestamp';

type Props = {
  notification: Notification;
  // Archived notifications can only be deleted (not archived again)
  isArchived?: boolean;
  isEditMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (notificationId: string) => void;
  onSwipeSelect?: (notificationId: string) => void;
};

// @TODO: fix weird swipe behaviour when scrolling
export function NotificationListItem({
  notification,
  isArchived = false,
  isEditMode = false,
  isSelected = false,
  onToggleSelect,
  onSwipeSelect,
}: Props) {
  const site = useSelector(selectSite);
  const navigate = useNavigate();
  const toast = useToast({ isClosable: true, position: 'top' });
  const title = useGetNotificationTitle(notification.notification_type);
  const {
    isOpen: isDeleteConfirmDialogOpen,
    onClose: onCloseDeleteConfirmDialog,
    onOpen: onOpenDeleteConfirmDialog,
  } = useDisclosure();
  const {
    isOpen: isArchiveConfirmDialogOpen,
    onClose: onCloseArchiveConfirmDialog,
    onOpen: onOpenArchiveConfirmDialog,
  } = useDisclosure();
  const [updateNotification] = useUpdateNotificationByIdMutation();
  const [deleteNotification] = useDeleteNotificationByIdMutation();
  const [shouldForceResetSlide, setShouldForceResetSlide] = useState(false);
  const borderColor = notification.severity === 'ALARM' ? '#DC0A0A' : '#3DCD57';
  const { t } = useTranslation();
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeLocked, setIsSwipeLocked] = useState(false); // locked to horizontal once decided
  const isCardOpenRef = useRef(false); // tracks whether the SwipeableSEHomeCard action buttons are visible

  useEffect(() => {
    // Instantly reset when enabled. Acts as a pseudo-event emitter.
    if (shouldForceResetSlide) setShouldForceResetSlide(false);
  }, [shouldForceResetSlide]);

  async function handleDeleteNotification() {
    try {
      await deleteNotification(notification.notification_id).unwrap();
      setShouldForceResetSlide(true);
    } catch {
      toast({
        title: t('Notifications.error deleting notification'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
    onCloseDeleteConfirmDialog();
  }

  async function handleArchiveNotification() {
    try {
      await updateNotification({ notificationId: notification.notification_id, body: { archived: true } }).unwrap();
      setShouldForceResetSlide(true);
    } catch {
      toast({
        title: t('Notifications.error archiving notification'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
    onCloseArchiveConfirmDialog();
  }

  function handleSwipeTouchStart(e: React.TouchEvent) {
    // Allow swiping on a selected card (to deselect it), but not on other cards while in edit mode.
    if ((isEditMode && !isSelected) || isCardOpenRef.current) return;
    const touch = e.touches[0];
    swipeStartRef.current = { x: touch.pageX, y: touch.pageY };
    setIsSwipeLocked(false);
  }

  function handleSwipeTouchMove(e: React.TouchEvent) {
    if (!swipeStartRef.current || (isEditMode && !isSelected) || isCardOpenRef.current) return;
    const touch = e.touches[0];
    const dx = touch.pageX - swipeStartRef.current.x;
    const dy = Math.abs(touch.pageY - swipeStartRef.current.y);

    // Once we decide direction, lock it
    if (!isSwipeLocked && (Math.abs(dx) > 10 || dy > 10)) {
      if (Math.abs(dx) > dy) {
        setIsSwipeLocked(true);
      } else {
        // Vertical scroll — abort swipe tracking
        swipeStartRef.current = null;
        return;
      }
    }

    if (isSwipeLocked) {
      // Clamp to 80px for visual feedback. A selected card is deselected by swiping LEFT (negative
      // offset); an unselected card is selected by swiping RIGHT (positive offset).
      if (isSelected && dx < 0) {
        setSwipeOffset(Math.max(dx, -80));
      } else if (!isSelected && dx > 0) {
        setSwipeOffset(Math.min(dx, 80));
      }
    }
  }

  function handleSwipeTouchEnd() {
    if (!swipeStartRef.current && !isSwipeLocked) return;
    // Swipe left past the threshold deselects a selected card; swipe right selects an unselected one.
    if (isSelected && swipeOffset < -50) {
      onToggleSelect?.(notification.notification_id);
    } else if (!isSelected && swipeOffset > 50) {
      onSwipeSelect?.(notification.notification_id);
    }
    setSwipeOffset(0);
    setIsSwipeLocked(false);
    swipeStartRef.current = null;
  }

  return (
    <>
      <Flex
        align="center"
        w="100%"
        onTouchStart={handleSwipeTouchStart}
        onTouchMove={handleSwipeTouchMove}
        onTouchEnd={handleSwipeTouchEnd}
        style={{
          transform: swipeOffset !== 0 ? `translateX(${swipeOffset}px)` : undefined,
          transition: swipeOffset === 0 ? 'transform 0.2s ease' : 'none',
          // Claim horizontal gestures for the card swipe so the browser never pans the page sideways with it
          touchAction: 'pan-y',
        }}
      >
        <Box
          overflow="hidden"
          transition="width 0.25s ease, margin 0.25s ease, opacity 0.25s ease"
          w={isEditMode || isSelected ? '32px' : '0px'}
          mr={isEditMode || isSelected ? 3 : 0}
          opacity={isEditMode || isSelected ? 1 : 0}
          flexShrink={0}
        >
          <CircleCheckbox
            isChecked={isSelected}
            onChange={() => onToggleSelect?.(notification.notification_id)}
            data-testid="notification-select-checkbox"
          />
        </Box>
        <SwipeableSEHomeCard
          containerProps={{
            'data-testid': 'notification-list-item-container',
            style: { flex: 1, minWidth: 0 },
          }}
          shouldForceResetSlide={shouldForceResetSlide}
          // While a card is selected, disable the archive/delete slide so swiping only toggles selection
          disableSlide={isSelected}
          swipeHintKey="notifications"
          onOpenStateChange={(isOpen) => {
            isCardOpenRef.current = isOpen;
          }}
          data-testid="notification-list-item"
          w={'100%'}
          onClick={
            isEditMode
              ? () => onToggleSelect?.(notification.notification_id)
              : () =>
                  navigate(
                    `/sites/${site.site_id}/notifications/${notification.notification_id}/view?direction=forward`,
                    {
                      state: { prevPath: window.location.pathname + `?archived=${isArchived}&direction=back` },
                    }
                  )
          }
          pl={0}
          borderTopLeftRadius="5px"
          mb={2}
          h="85px"
          borderLeft={!notification.read ? '6px solid' : undefined}
          borderColor={borderColor}
          primaryButtonProps={{
            onClick: onOpenDeleteConfirmDialog,
            'data-testid': 'delete-notification-btn',
            children: <DeleteIcon w="2rem" h="2rem" />,
          }}
          secondaryButtonProps={
            !isArchived
              ? {
                  onClick: onOpenArchiveConfirmDialog,
                  'data-testid': 'archive-notification-btn',
                  children: <ArchiveIcon w="2rem" h="2rem" />,
                }
              : undefined
          }
        >
          <Flex pl={4} pr={3} alignItems="center" h="100%">
            <Grid templateColumns={'1fr auto'} alignItems="center" flex={1} gap={2}>
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                  {notification.title || title}
                </Text>
                <Text fontSize="xs" noOfLines={1}>
                  {notification.short_content}
                </Text>
              </Box>
              <Flex flexDirection="column">
                <NotificationTimestamp notification={notification} />
              </Flex>
            </Grid>
          </Flex>
        </SwipeableSEHomeCard>
      </Flex>

      <OptionSelectAlertDrawer
        title={`${t('Notifications.this is permanent')} ${t('Common.are you sure?')}`}
        options={[{ label: t('Common.delete'), value: 'DELETE' }]}
        onSelectOption={handleDeleteNotification}
        isOpen={isDeleteConfirmDialogOpen}
        onClose={onCloseDeleteConfirmDialog}
      />

      <OptionSelectAlertDrawer
        title={`${t('Notifications.this is permanent')} ${t('Common.are you sure?')}`}
        options={[{ label: t('Notifications.archive'), value: 'ARCHIVE' }]}
        onSelectOption={handleArchiveNotification}
        isOpen={isArchiveConfirmDialogOpen}
        onClose={onCloseArchiveConfirmDialog}
      />
    </>
  );
}
